/** Privacy helpers shared by automatic telemetry collectors and tests. */

/** Keep route shape and query keys, but remove record IDs and query values. */
export function normalizeTelemetryPagePath(rawPath: string): string {
  const url = new URL(rawPath, "http://telemetry.local");
  const path = url.pathname
    .split("/")
    .map((segment) => {
      if (/^[0-9a-f]{24,}$/i.test(segment)) return ":id";
      if (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(segment)) return ":id";
      if (/^\d{5,}$/.test(segment)) return ":id";
      return segment;
    })
    .join("/")
    .slice(0, 180) || "/";

  const queryKeys = Array.from(new Set(
    Array.from(url.searchParams.keys())
      .filter((key) => /^[a-zA-Z0-9_-]{1,32}$/.test(key))
      .slice(0, 8),
  )).sort();

  return queryKeys.length > 0 ? `${path}?${queryKeys.join("&")}` : path;
}

/** Redact common accidental identifiers from semantic control labels. */
export function sanitizeTelemetryLabel(rawLabel: string): string {
  return rawLabel
    .replace(/https?:\/\/\S+/gi, "[url]")
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[email]")
    .replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ":id")
    .replace(/\b[0-9a-f]{24,}\b/gi, ":id")
    .replace(/\+?\d[\d\s().-]{7,}\d/g, "[number]")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 160);
}

/** Useful for completion/abandonment analysis without storing entered text. */
export function telemetryValueLengthBucket(length: number): string {
  if (length <= 0) return "0";
  if (length <= 4) return "1-4";
  if (length <= 20) return "5-20";
  if (length <= 100) return "21-100";
  return "101+";
}

export function telemetryViewport(): "mobile" | "tablet" | "desktop" {
  if (typeof window === "undefined") return "desktop";
  if (window.innerWidth < 768) return "mobile";
  if (window.innerWidth < 1200) return "tablet";
  return "desktop";
}

// React's useId() values (":r1u:", "«r1u»", "_r_1u_") change between renders and builds.
const REACT_GENERATED_ID = /^(?::r[0-9a-z]*:|«r[0-9a-z]*»|_r_[0-9a-z]*_)$/i;

/** Stable, human-readable field name for analytics, never the entered value. */
export function telemetryFieldName(
  control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
): string {
  const id = control.id && !REACT_GENERATED_ID.test(control.id) ? control.id : "";
  const label = control.labels?.[0]?.textContent?.replace(/[\s*\u2009]+$/u, "") ?? "";
  return sanitizeTelemetryLabel(
    control.dataset.telemetryLabel
      || control.name
      || id
      || control.getAttribute("aria-label")
      || label
      || control.getAttribute("placeholder")
      || control.tagName.toLocaleLowerCase("en"),
  );
}
